import styled from 'styled-components';
import { EASE, FADE, t } from 'theme';

/**
 * The page's own colours, and the only place they may live.
 *
 * They used to sit in the createGlobalStyle (Theme.style.js), which cannot hold a transition
 * across a theme change: styled-components replaces that sheet's rules wholesale when an
 * interpolation changes, and for one frame the rule is simply gone. Every declaration in it
 * reverts to the property's initial value in that frame, and the browser takes *that* as the
 * transition's starting point — so `background-color` began each crossover at `transparent`,
 * which reveals the browser canvas, which is white. The page snapped to hard white and then
 * spent the whole fade bringing alpha up toward a colour already indistinguishable from it.
 *
 * It read as a dawn-only bug because that is the only direction where it shows: white flashing
 * over an already-white page is invisible, and the dark then faded in over the top of it.
 *
 * A styled component does not have this problem. styled-components swaps a class on an element
 * that stays mounted, so the previous computed style survives and the transition has something
 * real to start from. Anything themed that needs to *move* belongs on a persistent element.
 */

/**
 * A fixed backdrop rather than a background on the wrapper, because `body` is 1400px wide and
 * centred (index.css) — a background painted inside it leaves the canvas showing down both
 * margins on a wide screen. This covers the viewport and sits behind everything.
 */
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: -1;
  background-color: ${t('bg')};
  transition: background-color ${FADE};
`;

/**
 * The app wrapper. Also carries --log-from / --log-to: the notification log's gradient can only
 * be crossfaded through registered custom properties (@property, index.css), and those have to
 * be declared somewhere persistent for the same reason as everything else here. They inherit
 * down to the log from this element.
 */
const Page = styled.div`
  --log-from: ${t('logFrom')};
  --log-to: ${t('logTo')};

  color: ${t('fg')};
  transition: color ${FADE}, --log-from ${FADE}, --log-to ${FADE};

  /* The map keeps its paper white and black strokes and is dimmed as a whole object, rather
     than having its internals re-themed — the label halos depend on the paper staying light.
     Written as a descendant rule from here rather than a global svg rule so the dimming
     rides on a persistent element and can fade with everything else. */
  svg {
    filter: ${t('mapFilter')};
    transition: filter ${FADE};
  }
`;

/**
 * The light closing in, on a traveler in the last band of his health.
 *
 * The game has no bar, no meter and no red, and it should not acquire any: the death it wants to
 * avoid is the one the player never saw coming, and a number going quietly from two to zero in a
 * row of other numbers is exactly that. This is the answer that costs no hue and teaches nothing —
 * the periphery of the screen darkens, the player's own vision narrows, and there is no legend to
 * read because there is nothing to read. It is the only loud thing in the interface, so it is
 * reserved for the last band and stays entirely absent above it.
 *
 * Two nested layers rather than one, because two different clocks meet here and the rules in
 * theme.js say a surface may only be on one:
 *
 *  - the outer layer carries the *theme's* depth (day is shallower than night) and fades on the
 *    shared FADE with every other surface, so a dusk that arrives while the player is dying moves
 *    in step with the rest of the page;
 *  - the inner layer carries the *band*, over a much longer duration, because a body failing is
 *    not a light being switched and should arrive slower than the weather.
 *
 * Multiplied together they give the right value at either end without transitioning a gradient,
 * which CSS cannot interpolate — the same trap that made the notification log snap at dusk.
 */
const CLOSING_MS = 2600;

const Closing = styled.div`
  position: fixed;
  inset: 0;
  /* Over the page, including an open event — a man reading a stranger's offer is still dying —
     but never in the way of a click. */
  z-index: 9;
  pointer-events: none;
  opacity: ${t('vignette')};
  transition: opacity ${FADE};
`;

/*
 * The geometry is the whole difference between this reading as vision closing and reading as
 * fog. An even ramp from the middle greys the entire page, which dims the health readout the
 * effect exists to point at and makes the game look like it is rendering badly.
 *
 * So: a wide clear core out to 45%, almost nothing through the middle distance, and the black
 * pushed into the outer quarter. The radii are set so the corners land past the last stop and go
 * fully dark while the top edge — where the header sits — is still only grazed. Centred a little
 * high for the same reason: the weight belongs below and to the sides, where nothing is written.
 */
const ClosingEdge = styled.div`
  position: absolute;
  inset: 0;
  background: radial-gradient(
    ellipse 58% 64% at 50% 44%,
    rgba(0, 0, 0, 0) 45%,
    rgba(0, 0, 0, 0.1) 66%,
    rgba(0, 0, 0, 0.52) 85%,
    rgba(0, 0, 0, 1) 100%
  );
  opacity: ${props => (props.$closed ? 1 : 0)};
  transition: opacity ${props => (t('reducedMotion')(props) ? 0 : CLOSING_MS)}ms ${EASE};
`;

export { Backdrop, Closing, ClosingEdge, Page };
